import React, { useState, useEffect } from "react";
import { Button, Modal, Tooltip, Input } from "antd";
import { CopyOutlined } from "@ant-design/icons";
import { types as at, errorAction } from "@authzsvc/api-lib";
import { cloneGroup, searchGroupIdentifier } from "../../actions/api";
import { useDispatch, useSelector } from "react-redux";
import { showErrorNotification, showSuccessNotification } from "../../util";

const IDENTIFIER_REGEX = /^[a-z][a-z0-9_-]{2,63}$/;

const CopyGroupButton = ({ canManage }) => {
  const dispatch = useDispatch();
  const selectedGroup = useSelector((state) => state.groups.selectedGroup);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [newGroupIdentifier, setNewGroupIdentifier] = useState(null);
  const [groupIdentifier, setGroupIdentifier] = useState("");
  const [checkingIdentifier, setCheckingIdentifier] = useState(false);
  const [identifierAvailable, setIdentifierAvailable] = useState(null); // null = unchecked, true = available, false = taken

  const isValidIdentifier = IDENTIFIER_REGEX.test(groupIdentifier.trim());

  useEffect(() => {
    const trimmed = groupIdentifier.trim();
    if (!IDENTIFIER_REGEX.test(trimmed)) {
      setIdentifierAvailable(null);
      setCheckingIdentifier(false);
      return;
    }
    let cancelled = false;
    setCheckingIdentifier(true);
    setIdentifierAvailable(null);
    const timer = setTimeout(async () => {
      try {
        const result = await dispatch(
          searchGroupIdentifier("GroupIdentifier", [trimmed])
        );
        if (cancelled) return;
        const found = result?.payload?.data?.find((o) => o.value === trimmed);
        setIdentifierAvailable(found ? !found.isTaken : null);
      } catch {
        if (!cancelled) setIdentifierAvailable(null);
      }
      if (!cancelled) setCheckingIdentifier(false);
    }, 600);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [groupIdentifier, dispatch]);

  const handleCopyGroup = async () => {
    setLoading(true);
    const result = await dispatch(
      cloneGroup(selectedGroup.id, groupIdentifier.trim())
    );
    if (result.type === errorAction(at.CLONE_GROUP)) {
      const apiMessage = result?.payload?.data?.message || "";
      const description = apiMessage.includes("display_name_key")
        ? "A group with a similar display name already exists. Please choose a different identifier."
        : apiMessage || "Sorry, something went wrong";
      showErrorNotification(
        result?.payload?.title || "Failed to clone group",
        description
      );
    } else {
      showSuccessNotification("Group copied successfully");
      setNewGroupIdentifier(groupIdentifier);
      handleClose();
      setShowSuccessModal(true);
    }
    setLoading(false);
  };

  const handleClose = () => {
    setShowModal(false);
    setGroupIdentifier("");
    setIdentifierAvailable(null);
  };

  return (
    <>
      <Tooltip title="Copy the group's values, including its membership and all settings.">
        <Button
          id="CopyGroupButton"
          icon={<CopyOutlined />}
          onClick={() => setShowModal(true)}
          disabled={selectedGroup?.securityIssues || !canManage}
        >
          Copy
        </Button>
      </Tooltip>
      <Modal
        title="Copy Group"
        open={showModal}
        onOk={handleCopyGroup}
        onCancel={handleClose}
        okText="Copy"
        okButtonProps={{
          loading: loading,
          disabled:
            !isValidIdentifier ||
            checkingIdentifier ||
            identifierAvailable !== true,
        }}
        cancelButtonProps={{ disabled: loading }}
      >
        <p>
          Your group will be copied, including its membership and all settings.
          Please choose the group identifier for the new group.
        </p>
        <Input
          placeholder="Set Group Identifier"
          disabled={loading}
          value={groupIdentifier}
          onChange={(e) => setGroupIdentifier(e.target.value)}
          status={
            (groupIdentifier.length > 0 && !isValidIdentifier) ||
            identifierAvailable === false
              ? "error"
              : isValidIdentifier && identifierAvailable === true
              ? "success"
              : ""
          }
        />
        {groupIdentifier.length > 0 && !isValidIdentifier && (
          <div style={{ color: "#ff4d4f", fontSize: 12, marginTop: 4 }}>
            Must start with a lowercase letter, contain only lowercase letters,
            numbers, dashes or underscores, and be 3–64 characters.
          </div>
        )}
        {isValidIdentifier && identifierAvailable === false && (
          <div style={{ color: "#ff4d4f", fontSize: 12, marginTop: 4 }}>
            This identifier is already in use.
          </div>
        )}
        {checkingIdentifier && (
          <div style={{ fontSize: 12, marginTop: 4, color: "#888" }}>
            Checking availability...
          </div>
        )}
      </Modal>
      <Modal
        title="Group Copied Successfully"
        open={showSuccessModal}
        onCancel={() => setShowSuccessModal(false)}
        footer={
          <Button onClick={() => setShowSuccessModal(false)}>Close</Button>
        }
      >
        <p>
          Please visit the{" "}
          {newGroupIdentifier ? (
            <a href={`/group/${newGroupIdentifier}/details`}>new group</a>
          ) : (
            "new group [link unavailable]"
          )}{" "}
          to verify that all settings and memberships have been copied
          correctly. If you notice any issues, please contact{" "}
          <a href="https://cern.service-now.com/service-portal">Service Desk</a>
          .
        </p>
      </Modal>
    </>
  );
};

export default CopyGroupButton;
